import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getMember } from '../data/team';
import { PAGES } from '../seo/pages';
import {
  SITE_URL as SITE,
  memberUrl,
  memberTitle,
  memberDescription,
  personJsonLd,
  breadcrumbJsonLd
} from '../seo/personSchema';

// The production domain lives in src/seo/personSchema.js, which is shared with
// the build scripts. Re-exported here so existing imports keep working.
export const SITE_URL = SITE;
const OG_IMAGE = `${SITE_URL}/logo.png`;
const SITE_NAME = 'Tabia Group';

// /equipo/<slug> — one indexable page per team member.
const MEMBER_ROUTE = /^\/equipo\/([a-z0-9-]+)$/;

// Per-page SEO table lives in src/seo/pages.js so the build scripts can read
// it too. Re-exported here so existing imports keep working.
export { PAGES };

function findEntry(pathname) {
  return PAGES.find((p) => p.es === pathname || p.en === pathname);
}

function Seo() {
  const { pathname } = useLocation();

  // Team member profiles are generated from the team data rather than PAGES.
  const memberMatch = pathname.match(MEMBER_ROUTE);
  const member = memberMatch ? getMember(memberMatch[1]) : undefined;

  if (member) {
    const title = memberTitle(member);
    const description = memberDescription(member);
    const canonical = memberUrl(member.slug);
    const image = member.pendingPhoto ? OG_IMAGE : `${SITE_URL}/${member.image}`;

    return (
      <Helmet>
        <html lang="es" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />

        {/* Spanish-only pages: no English alternate to advertise. */}
        <link rel="alternate" hrefLang="es" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />

        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={image} />
        <meta property="og:locale" content="es_ES" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />

        <script type="application/ld+json">{JSON.stringify(personJsonLd(member))}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd(member))}</script>
      </Helmet>
    );
  }

  const entry = findEntry(pathname);

  // Unknown route (404): index nothing, keep it generic.
  if (!entry) {
    return (
      <Helmet>
        <html lang="es" />
        <title>Página no encontrada | Tabia Group</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
    );
  }

  const isEn = entry.en === pathname;
  const lang = isEn ? 'en' : 'es';
  const title = isEn ? entry.enTitle : entry.esTitle;
  const description = isEn ? entry.enDesc : entry.esDesc;
  const canonical = `${SITE_URL}${pathname}`;
  const ogLocale = isEn ? 'en_US' : 'es_ES';

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* hreflang alternates for the bilingual pair */}
      <link rel="alternate" hrefLang="es" href={`${SITE_URL}${entry.es}`} />
      {entry.en && (
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}${entry.en}`} />
      )}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${entry.es}`} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </Helmet>
  );
}

export default Seo;
